import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import LogCard from './LogCard';
import { logsApi } from '../../../api/logs';
import { Pagination } from 'flowbite-react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { toCurrency } from '../../../utils/helper';

const logList = [
  'DEPOSIT',
  'EARNED',
  'WITHDRAW',
  'WALLET',
  'COMMISSION',
  'COMMISSION TO WALLET',
  'LOAD REQUEST',
  'WIN',
  'FLOAT',
];

export default function Log({ userId }) {
  const params = useParams();
  // Use userId prop if provided, otherwise fall back to route params
  const id = userId || params.id;
  const { tokens } = useAuth();
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await logsApi.getLogsById(
          tokens.access.token,
          id,
          currentPage,
          10,
          selectedFilter
        );

        if (response.status === 200) {
          setLogs(response.data.rows);
          setTotalPages(Math.ceil(response.data.count / 10));
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [currentPage, id, tokens.access.token, selectedFilter]);

  return (
    <>
      <div className='pt-4'>
        <div className='flex gap-2.5'>
          <div className='w-1/3 max-xs:w-1/2'>{/* <Calendar /> */}</div>
          <div className='w-2/3 max-xs:w-1/2'>
            <label htmlFor='filter' className='text-xs font-medium '>
              Filter
            </label>
            <div className='relative w-full'>
              <button
                id='dropdownButton'
                className='w-full p-2.5 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center'
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span>{selectedFilter || 'All'}</span>
                <span className='text-gray-500'>
                  {dropdownOpen ? (
                    <ChevronUp className='h-4 w-4' />
                  ) : (
                    <ChevronDown className='h-4 w-4' />
                  )}
                </span>
              </button>
              {dropdownOpen && (
                <ul className='absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md'>
                  <li
                    onClick={() => {
                      setSelectedFilter('');
                      setDropdownOpen(false);
                    }}
                    className='p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer'
                  >
                    All
                  </li>
                  {logList.map((type) => (
                    <li
                      key={type}
                      onClick={() => {
                        setSelectedFilter(type);
                        setDropdownOpen(false);
                      }}
                      className='p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer border-t border-[#E1E7EF]'
                    >
                      {type}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='pt-4'>
        <div
          className='divide-y divide-gray-200 overflow-y-auto hide-scrollbar'
          style={{ height: 'calc(100vh - 150px)' }}
        >
          {logs.map((log) => (
            <LogCard
              key={log.id}
              action={log.action}
              type={log.action}
              title={log.type}
              amount={{
                price: `${log.action === 'DEDUCTED' ? '-' : ''}₱${toCurrency(
                  log.value
                )}`,
                color: log.action === 'DEDUCTED' ? 'red-500' : 'green-400',
              }}
              date={new Date(log.createdAt).toLocaleString()}
              reference={log.otherData?.balance}
              otherData={log.otherData}
            />
          ))}
        </div>

        <div className='flex justify-center py-4'>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            showIcons
            className='pagination'
          />
        </div>
      </div>
    </>
  );
}
